import QRCodeLib from "qrcode";
import { useEffect, useState, useMemo } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";

import { collection, getDocs, query, where, addDoc, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Table, Loader, Button, Text, Tooltip, Divider, Space, Modal, Group, ActionIcon, Badge, TextInput, ThemeIcon, Menu } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import FormSelectOptionsModal from "./form-templates/form-select-options-modal";
import FormOptionsBuilderModal from "./form-templates/form-options-builder-modal";
import SubmissionMappingsModal from "./form-templates/submission-mappings-modal";
import { Edit, Plus, Qrcode, Trash, Copy, Refresh, TransferIn, InfoCircle, AlertCircle } from "tabler-icons-react";
import { IconDotsVertical, IconX } from "@tabler/icons-react";
import { useDuplicateOptionsSet } from "../hooks/useDuplicateOptionsSet";
import {
  useFormOptionsSetBuilder,
  getSyncDiffForOptionsSet,
  executeSync,
  SyncDiffResult,
} from "../hooks/useFormOptionsSetBuilder";
import OptionSyncDiffModal from "../components/modals/option-sync-diff-modal";
import { fetchModulesFromFirestore } from "../hooks/modules";
import { FlexFormFirestoreSelectOptionSet, FlexFormTemplate, FormFieldConfig, OptionsSourceType } from "@captiva/shared-types";
import { PageHeader } from "../components/shared";

export interface FormSelectOptionsProps {
  companyId: string;
}

const getModuleColor = (moduleName: string | null | undefined): string => {
  if (!moduleName) return "gray";
  const colors = ["blue", "cyan", "grape", "green", "indigo", "orange", "pink", "red", "teal", "violet"];
  let hash = 0;
  for (let i = 0; i < moduleName.length; i++) {
    hash = moduleName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const getModuleName = (docObj: FlexFormFirestoreSelectOptionSet, modulesMap: Record<string, string>): string | null => {
  if (docObj.metadata?.originalModuleName) {
    return docObj.metadata.originalModuleName;
  }
  if ((docObj as any).moduleMetadata?.originalModuleName) {
    return (docObj as any).moduleMetadata.originalModuleName;
  }
  if (docObj.moduleId) {
    if (modulesMap[docObj.moduleId]) {
      return modulesMap[docObj.moduleId];
    }
    if (docObj.moduleId === "SzwgMTEF3PL3FOppgFGR") {
      return "Time and Attendance";
    }
    return docObj.moduleId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }
  return null;
};

export default function FormSelectOptions({ companyId }: FormSelectOptionsProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [optionsDocs, setOptionsDocs] = useState<FlexFormFirestoreSelectOptionSet[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [modulesMap, setModulesMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editingDoc, setEditingDoc] = useState<FlexFormFirestoreSelectOptionSet | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allTemplates, setAllTemplates] = useState<FlexFormTemplate[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [pendingDeleteDoc, setPendingDeleteDoc] = useState<FlexFormFirestoreSelectOptionSet | null>(null);
  const [inUseModalOpen, setInUseModalOpen] = useState<boolean>(false);
  const [inUseDoc, setInUseDoc] = useState<FlexFormFirestoreSelectOptionSet | null>(null);
  const [inUseTemplates, setInUseTemplates] = useState<FlexFormTemplate[]>([]);

  const { duplicateOptionsSet, duplicating } = useDuplicateOptionsSet(companyId);

  const [builderOpen, setBuilderOpen] = useState<boolean>(false);
  const [syncingDocId, setSyncingDocId] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [mappingsModalOpen, setMappingsModalOpen] = useState<boolean>(false);
  const [mappingDoc, setMappingDoc] = useState<FlexFormFirestoreSelectOptionSet | null>(null);

  const openMappingsModal = (docObj: FlexFormFirestoreSelectOptionSet) => {
    setMappingDoc(docObj);
    setMappingsModalOpen(true);
  };

  const {
    syncExistingOptionsSet,
    getSyncDiffForOptionsSet: hookGetSyncDiff,
    executeSync: hookExecuteSync,
  } = useFormOptionsSetBuilder();

  const getDiffFn = hookGetSyncDiff || getSyncDiffForOptionsSet;
  const executeSyncFn = hookExecuteSync || executeSync;

  const [diffModalOpen, setDiffModalOpen] = useState<boolean>(false);
  const [targetSyncDoc, setTargetSyncDoc] = useState<FlexFormFirestoreSelectOptionSet | null>(null);
  const [syncDiffResult, setSyncDiffResult] = useState<SyncDiffResult | null>(null);
  const [diffModalLoading, setDiffModalLoading] = useState<boolean>(false);

  const handleSync = async (docObj: FlexFormFirestoreSelectOptionSet) => {
    if (!docObj || syncingDocId) return;
    setSyncingDocId(docObj.id);
    setTargetSyncDoc(docObj);
    setSyncDiffResult(null);
    setDiffModalLoading(true);
    setDiffModalOpen(true);
    setNotification(null);
    try {
      const res = await getDiffFn(docObj.id, companyId);
      setSyncDiffResult(res.diff);
    } catch (err: any) {
      console.error("Failed to calculate sync diff:", err);
      setNotification({
        type: "error",
        message: `Failed to calculate sync diff for "${docObj.name}": ${err.message}`,
      });
      setTimeout(() => setNotification(null), 4000);
      setDiffModalOpen(false);
    } finally {
      setDiffModalLoading(false);
      setSyncingDocId(null);
    }
  };

  const handleConfirmSync = async (overrideZeroGuard?: boolean) => {
    if (!targetSyncDoc || !syncDiffResult) return;
    setDiffModalLoading(true);
    try {
      const res = await executeSyncFn(targetSyncDoc.id, syncDiffResult, overrideZeroGuard);
      if (res.success) {
        setNotification({
          type: "success",
          message: `Successfully synchronized "${targetSyncDoc.name}"! Set now contains ${res.count} options.`,
        });
        setTimeout(() => setNotification(null), 4000);
        setDiffModalOpen(false);
        setTargetSyncDoc(null);
        setSyncDiffResult(null);

        const q = query(collection(db, "flexFormSelectOptions"), where("companyId", "==", companyId));
        const snapshot = await getDocs(q);
        const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FlexFormFirestoreSelectOptionSet);
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setOptionsDocs(list);
      }
    } catch (err: any) {
      console.error(err);
      setNotification({
        type: "error",
        message: `Sync failed for "${targetSyncDoc.name}": ${err.message}`,
      });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setDiffModalLoading(false);
    }
  };

  const handleCloseDiffModal = () => {
    if (diffModalLoading) return;
    setDiffModalOpen(false);
    setTargetSyncDoc(null);
    setSyncDiffResult(null);
  };

  useEffect(() => {
    if (!companyId) return;

    setLoading(true);
    const q = query(collection(db, "flexFormSelectOptions"), where("companyId", "==", companyId));
    getDocs(q).then((snapshot) => {
      const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FlexFormFirestoreSelectOptionSet);
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setOptionsDocs(list);
      setLoading(false);
    });

    fetchTemplates();
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    fetchModulesFromFirestore().then((modules) => {
      const map: Record<string, string> = {};
      modules.forEach(m => {
        map[m.id] = m.name;
      });
      setModulesMap(map);
    }).catch(err => console.error(err));
  }, [companyId]);

  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      const q = query(collection(db, "flexFormTemplates"), where("companyId", "==", companyId));
      const snapshot = await getDocs(q);
      const list = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }) as FlexFormTemplate)
        .filter((t) => t.archived !== true);
      setAllTemplates(list);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openManager = async (docObj: FlexFormFirestoreSelectOptionSet | null = null) => {
    await fetchTemplates();
    setEditingDoc(docObj);
    setModalOpen(true);
  };

  const saveDoc = async (docData: { name: string; options: any[] }) => {
    const { name, options } = docData;
    if (editingDoc) {
      await updateDoc(doc(db, "flexFormSelectOptions", editingDoc.id), { name, options, companyId });
    } else {
      await addDoc(collection(db, "flexFormSelectOptions"), { name, options, companyId });
    }
    setModalOpen(false);
    setEditingDoc(null);
    const q = query(collection(db, "flexFormSelectOptions"), where("companyId", "==", companyId));
    const snapshot = await getDocs(q);
    const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FlexFormFirestoreSelectOptionSet);
    list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    setOptionsDocs(list);
  };

  const handleDuplicate = async (docObj: FlexFormFirestoreSelectOptionSet) => {
    try {
      await duplicateOptionsSet(docObj);

      const q = query(collection(db, "flexFormSelectOptions"), where("companyId", "==", companyId));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FlexFormFirestoreSelectOptionSet);
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setOptionsDocs(list);
    } catch (error) {
      console.error("Error duplicating options set:", error);
    }
  };

  const handleDelete = (docObj: FlexFormFirestoreSelectOptionSet) => {
    if (!docObj) return;
    const used = getUsedTemplates(docObj.id);
    if (used.length > 0) {
      setInUseDoc(docObj);
      setInUseTemplates(used);
      setInUseModalOpen(true);
    } else {
      setPendingDeleteDoc(docObj);
      setDeleteConfirmOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDeleteDoc) return;
    await deleteDoc(doc(db, "flexFormSelectOptions", pendingDeleteDoc.id));
    setOptionsDocs(optionsDocs.filter((d) => d.id !== pendingDeleteDoc.id));
    setDeleteConfirmOpen(false);
    setPendingDeleteDoc(null);
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPendingDeleteDoc(null);
  };

  const getUsedTemplates = (docId: string) => {
    return allTemplates.filter((t) =>
      Array.isArray(t.fields) &&
      t.fields.some((f: FormFieldConfig) => {
        const optSrc = f?.optionsSource;
        if (!optSrc || optSrc.type !== OptionsSourceType.FIRESTORE) return false;
        return (
          optSrc.document === docId ||
          optSrc.id === docId ||
          optSrc.optionsSetId === docId
        );
      })
    );
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', width: '100%' }}>
      <Loader size="lg" color="cap-blue" />
    </div>
  );

  return (
    <>
      {notification && (
        <div style={{ position: "fixed", top: 76, right: 20, zIndex: 9999 }}>
          <Button color={notification.type === "success" ? "green" : "red"} variant="light" onClick={() => setNotification(null)}>
            {notification.message}
          </Button>
        </div>
      )}
      <Modal
        opened={deleteConfirmOpen}
        onClose={cancelDelete}
        title="Confirm Delete"
        centered
      >
        <Text mb="md">
          Are you sure you want to delete "{pendingDeleteDoc?.name}"? This action cannot be undone.
        </Text>
        <Group position="right">
          <Button variant="outline" color="gray" onClick={cancelDelete}>Cancel</Button>
          <Button variant="filled" color="red" onClick={confirmDelete}>Delete</Button>
        </Group>
      </Modal>
      <Modal
        opened={inUseModalOpen}
        onClose={() => {
          setInUseModalOpen(false);
          setInUseDoc(null);
          setInUseTemplates([]);
        }}
        title={
          <Group spacing="xs">
            <ThemeIcon color="orange" variant="light" size="md">
              <AlertCircle size={18} />
            </ThemeIcon>
            <Text weight={600}>Cannot Delete Option Set</Text>
          </Group>
        }
        centered
      >
        <Text mb="sm" size="sm">
          <strong>"{inUseDoc?.name}"</strong> cannot be deleted because it is currently in use by the following form template{inUseTemplates.length > 1 ? "s" : ""}:
        </Text>
        <div style={{ maxHeight: 180, overflowY: "auto", margin: "0.5em 0 1em" }}>
          <ul style={{ margin: 0, paddingLeft: "1.5em" }}>
            {inUseTemplates.map((t) => (
              <li key={t.id} style={{ marginBottom: "0.25em" }}>
                <Text size="sm" weight={500}>{t.name || "Untitled Form"}</Text>
              </li>
            ))}
          </ul>
        </div>
        <Text size="sm" color="dimmed" mb="lg">
          Please remove or replace this option set in the template(s) listed above before deleting it.
        </Text>
        <Group position="right">
          <Button
            variant="filled"
            color="cap-blue"
            onClick={() => {
              setInUseModalOpen(false);
              setInUseDoc(null);
              setInUseTemplates([]);
            }}
          >
            Understood
          </Button>
        </Group>
      </Modal>
      <PageHeader
        title="Option Sets"
        subtitle="Manage shared dropdown options and selection sets"
        action={
          <Group>
            <Button color="cap-blue" variant="outline" onClick={() => setBuilderOpen(true)} leftIcon={<TransferIn size={16} />}>
              Build from submissions
            </Button>
            <Button color="cap-blue" variant="filled" onClick={() => openManager()} leftIcon={<Plus size={16} />}>
              Options set
            </Button>
          </Group>
        }
      />
      <Space h="sm" />
      <div style={{ marginBottom: "1.5em", width: "100%", maxWidth: "400px" }}>
        <TextInput
          placeholder="Search by option set name or option label..."
          label="Search Options Sets"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
          rightSection={
            searchTerm ? (
              <ActionIcon
                size="xs"
                color="gray"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
        />
      </div>
      
      {(() => {
        const filteredOptionsDocs = optionsDocs.filter((docObj) => {
          if (!searchTerm.trim()) return true;
          const term = searchTerm.toLowerCase();
          const matchesName = (docObj.name || "").toLowerCase().includes(term);
          const matchesOption = docObj.options?.some((opt: any) =>
            (opt.label || "").toLowerCase().includes(term) ||
            String(opt.value ?? "").toLowerCase().includes(term)
          );
          return matchesName || matchesOption;
        });

        if (filteredOptionsDocs.length === 0) {
          return (
            <Text size="sm" color="dimmed" style={{ margin: "1em auto", width: "100%", textAlign: "center" }}>
              No options sets match your search.
            </Text>
          );
        }

        return (
          <Table striped highlightOnHover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Options Count</th>
                <th>Module</th>
                <th> </th>
              </tr>
            </thead>
            <tbody>
              {filteredOptionsDocs.map((docObj) => {
                const term = searchTerm.trim().toLowerCase();
                const matchingOptions = term
                  ? (docObj.options || []).filter((opt: any) =>
                      (opt.label || "").toLowerCase().includes(term) ||
                      String(opt.value ?? "").toLowerCase().includes(term)
                    )
                  : [];

                return (
                  <tr key={docObj.id}>
                    <td>
                      <div>
                        <Group spacing={8}>
                          <Text weight={500}>{docObj.name}</Text>
                          {(docObj as any).linkedFormTemplateId && (
                            <Badge
                              size="xs"
                              color={(docObj as any).autoSync !== false ? "teal" : "gray"}
                              variant="light"
                            >
                              {(docObj as any).autoSync !== false ? "Auto-sync" : "Manual sync"}
                            </Badge>
                          )}
                        </Group>
                        {matchingOptions.length > 0 && (
                          <Group spacing={4} mt={2}>
                            <Text size="xs" color="dimmed">Matched option:</Text>
                            {matchingOptions.slice(0, 3).map((opt: any, i: number) => (
                              <Badge key={i} size="xs" variant="outline" color="blue">
                                {opt.label || opt.value}
                              </Badge>
                            ))}
                            {matchingOptions.length > 3 && (
                              <Text size="xs" color="dimmed">+{matchingOptions.length - 3} more</Text>
                            )}
                          </Group>
                        )}
                      </div>
                    </td>
                    <td>{docObj.options?.length || 0}</td>
                  <td>
                    {(() => {
                      const modName = getModuleName(docObj, modulesMap);
                      return modName ? (
                        <Badge color={getModuleColor(modName)} variant="light">
                          {modName}
                        </Badge>
                      ) : "-";
                    })()}
                  </td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                      {(() => {
                        const usedTemplates = getUsedTemplates(docObj.id);
                        const isInUse = usedTemplates.length > 0;
                        const deleteTooltip = isInUse
                          ? `In use by: ${usedTemplates.map((t) => t.name).join(", ")}`
                          : "Delete options set";

                        if (isMobile) {
                          return (
                            <Menu
                              size="sm"
                              position="bottom"
                              placement="end"
                              withinPortal
                              control={
                                <ActionIcon
                                  size={44}
                                  variant="outline"
                                  color="cap-blue"
                                  aria-label={`Actions for ${docObj.name}`}
                                  sx={{ minWidth: 44, minHeight: 44 }}
                                >
                                  <IconDotsVertical size={20} />
                                </ActionIcon>
                              }
                            >
                              <Menu.Item
                                icon={<Edit size={16} />}
                                onClick={() => openManager(docObj)}
                              >
                                Edit
                              </Menu.Item>
                              <Menu.Item
                                icon={<Copy size={16} />}
                                onClick={() => handleDuplicate(docObj)}
                                disabled={duplicating}
                              >
                                Duplicate
                              </Menu.Item>
                              <Menu.Item
                                icon={<Qrcode size={16} />}
                                onClick={() => generateAndDownloadQRCodesForSet(docObj)}
                              >
                                QR Codes
                              </Menu.Item>
                              {(docObj as any).linkedFormTemplateId && (
                                <>
                                  <Menu.Item
                                    icon={<Refresh size={16} />}
                                    onClick={() => handleSync(docObj)}
                                    disabled={syncingDocId === docObj.id}
                                  >
                                    Synchronize
                                  </Menu.Item>
                                  <Menu.Item
                                    icon={<InfoCircle size={16} />}
                                    onClick={() => openMappingsModal(docObj)}
                                  >
                                    Mappings
                                  </Menu.Item>
                                </>
                              )}
                              <Divider />
                              <Menu.Item
                                color={isInUse ? "gray" : "red"}
                                icon={<Trash size={16} />}
                                onClick={() => handleDelete(docObj)}
                              >
                                Delete
                              </Menu.Item>
                            </Menu>
                          );
                        }

                        return (
                          <>
                            {(docObj as any).linkedFormTemplateId && (
                              <>
                                <Tooltip label="Synchronize options from form submissions" withArrow position="top">
                                  <ActionIcon
                                    size="lg"
                                    color="green"
                                    variant="outline"
                                    onClick={() => handleSync(docObj)}
                                    loading={syncingDocId === docObj.id}
                                  >
                                    <Refresh size={18} />
                                  </ActionIcon>
                                </Tooltip>
                                <Tooltip label="View submission-option mappings" withArrow position="top">
                                  <ActionIcon
                                    size="lg"
                                    color="blue"
                                    variant="outline"
                                    ml="md"
                                    onClick={() => openMappingsModal(docObj)}
                                  >
                                    <InfoCircle size={18} />
                                  </ActionIcon>
                                </Tooltip>
                              </>
                            )}
                            <ActionIcon size="lg" color="cap-blue" variant="outline" ml="md" aria-label={`Generate QR codes for ${docObj.name}`} onClick={() => generateAndDownloadQRCodesForSet(docObj)}>
                              <Qrcode size={18} />
                            </ActionIcon>
                            <ActionIcon size="lg" color="cap-blue" variant="outline" ml="md" aria-label={`Edit ${docObj.name}`} onClick={() => openManager(docObj)}>
                              <Edit size={18} />
                            </ActionIcon>
                            <ActionIcon size="lg" color="cap-blue" variant="outline" ml="md" aria-label={`Duplicate ${docObj.name}`} onClick={() => handleDuplicate(docObj)} loading={duplicating} disabled={duplicating}>
                              <Copy size={18} />
                            </ActionIcon>
                            <Tooltip label={deleteTooltip} withArrow position="top">
                              <ActionIcon
                                size="lg"
                                color={isInUse ? "gray" : "red"}
                                variant="outline"
                                ml="md"
                                onClick={() => handleDelete(docObj)}
                                aria-label={`Delete ${docObj.name}`}
                              >
                                <Trash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              );
            })}
            </tbody>
          </Table>
        );
      })()}
      {modalOpen && !isLoading && (
        <FormSelectOptionsModal
          opened={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={saveDoc}
          initialDoc={editingDoc}
        />
      )}
      {builderOpen && (
        <FormOptionsBuilderModal
          opened={builderOpen}
          onClose={() => setBuilderOpen(false)}
          onSave={() => {
            setBuilderOpen(false);
            const q = query(collection(db, "flexFormSelectOptions"), where("companyId", "==", companyId));
            getDocs(q).then((snapshot) => {
              const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FlexFormFirestoreSelectOptionSet);
              list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
              setOptionsDocs(list);
            });
            setNotification({
              type: "success",
              message: "Options set built successfully!",
            });
            setTimeout(() => setNotification(null), 4000);
          }}
          companyId={companyId}
        />
      )}
      {isLoading && <Loader color="cap-navy" style={{ margin: "2em auto", display: "block" }} />}
      {mappingsModalOpen && (
        <SubmissionMappingsModal
          opened={mappingsModalOpen}
          onClose={() => {
            setMappingsModalOpen(false);
            setMappingDoc(null);
          }}
          optionsDoc={mappingDoc}
          companyId={companyId}
          onSyncSuccess={async () => {
            const q = query(collection(db, "flexFormSelectOptions"), where("companyId", "==", companyId));
            const snapshot = await getDocs(q);
            const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FlexFormFirestoreSelectOptionSet);
            list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            setOptionsDocs(list);
            if (mappingDoc) {
              const updated = list.find((d) => d.id === mappingDoc.id);
              if (updated) setMappingDoc(updated);
            }
          }}
        />
      )}
      <OptionSyncDiffModal
        opened={diffModalOpen}
        onClose={handleCloseDiffModal}
        onConfirm={handleConfirmSync}
        diff={syncDiffResult}
        optionsSetName={targetSyncDoc?.name || ""}
        loading={diffModalLoading}
      />
    </>
  );
}

async function generateAndDownloadQRCodesForSet(optionsDoc: FlexFormFirestoreSelectOptionSet) {
  if (!optionsDoc || !optionsDoc.options || !optionsDoc.options.length) return;
  const zip = new JSZip();
  const container = document.createElement('div');
  container.style.display = 'none';
  document.body.appendChild(container);
  for (const option of optionsDoc.options) {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;
    container.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    if (!ctx) continue;
    const qrValue = `${option.label}:${option.value}`;
    await QRCodeLib.toCanvas(canvas, qrValue, {
      width: 400,
      margin: 10,
      errorCorrectionLevel: 'H'
    });
    const logoImg = new window.Image();
    logoImg.src = '/captiva-mark.png';
    await new Promise((resolve) => {
      logoImg.onload = resolve;
      logoImg.onerror = resolve;
    });
    const logoX = 20;
    const logoY = 20;
    const logoSize = 40;
    ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
    ctx.font = "bold 16px 'Helvetica Neue', Helvetica, Arial, sans-serif";
    ctx.fillStyle = "#333";
    ctx.textBaseline = "middle";
    ctx.fillText(`${optionsDoc.name || ""} - ${option.label}`, logoX + logoSize + 16, logoY + logoSize / 2);
    const pngUrl = canvas.toDataURL("image/png");
    zip.file(`${option.label}-${option.value}-qr.png`, pngUrl.substring(pngUrl.indexOf(',') + 1), { base64: true });
    container.removeChild(canvas);
  }
  document.body.removeChild(container);
  const zipBlob = await zip.generateAsync({ type: "blob" });
  saveAs(zipBlob, `${optionsDoc.name || "options"}-qr-codes.zip`);
}